import { Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { faqPageSchema, webPageSchema } from '../../../core/seo/structured-data';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';

const PAGE_PATH = '/why-trust-us';
const PAGE_TITLE = 'Why Trust SavingArena? Verified Coupons You Can Rely On';
const PAGE_DESCRIPTION =
  'Why shoppers trust SavingArena: verified coupons, secure deals and a six-step check on every offer. Here is what makes us a name you can rely on.';

interface VerifyStep {
  title: string;
  description: string;
}

interface TrustFeature {
  title: string;
  description: string;
}

interface TrustFaq {
  question: string;
  answer: string;
}

@Component({
  selector: 'app-why-trust-us-page',
  imports: [Breadcrumb, RouterLink, BigSaleBanner, DevFlag],
  templateUrl: './why-trust-us-page.html',
  styleUrl: './why-trust-us-page.scss',
})
export class WhyTrustUsPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'Why Trust Us' });

  private readonly seo = inject(SeoService);

  protected readonly steps: VerifyStep[] = [
    {
      title: 'Source Validation',
      description: 'We partner with trusted brands and retailers to source authentic coupons directly.',
    },
    {
      title: 'Manual Review',
      description: 'Our team manually reviews each coupon to ensure it meets our quality standards.',
    },
    {
      title: 'Automated Testing',
      description: 'We use advanced tools to test coupon codes for validity and usability.',
    },
    {
      title: 'User Feedback',
      description: 'We monitor user feedback to identify and remove any non-working coupons.',
    },
    {
      title: 'Regular Updates',
      description: 'Our database is updated regularly to ensure all coupons are current and valid.',
    },
    {
      title: 'Security Checks',
      description: 'We perform security checks to ensure coupons are safe and free from fraud.',
    },
  ];

  protected readonly features: TrustFeature[] = [
    { title: 'Verified Coupons', description: 'We ensure every coupon is tested and verified before listing.' },
    { title: 'User-Centric Approach', description: 'Your satisfaction is our priority. We value your feedback and act on it.' },
    { title: 'Secure & Safe', description: 'We prioritize your security and ensure all coupons are safe to use.' },
    { title: 'Transparent Process', description: 'We maintain complete transparency in how we source and verify coupons.' },
    { title: 'Dedicated Support', description: 'Our customer support team is available to assist you with any issues.' },
    { title: 'Reputable Partnerships', description: 'We collaborate with well-known brands to provide exclusive deals.' },
    { title: 'Real-Time Updates', description: 'Our system ensures real-time updates to remove expired or invalid coupons.' },
    { title: 'High Success Rate', description: 'Our coupons have a high redemption success rate, ensuring you save effortlessly.' },
    { title: 'Community-Driven', description: 'We actively engage with our user community to improve our services.' },
    { title: 'Data Privacy', description: 'We prioritize your privacy and never share your personal information.' },
    { title: 'Proven Track Record', description: 'Trusted by millions of users who have saved significantly using our coupons.' },
    { title: 'Educational Resources', description: 'We provide tips and guides to help you make the most of your savings.' },
    { title: 'Ethical Practices', description: 'We adhere to strict ethical standards to ensure fairness and reliability.' },
    { title: 'Exclusive Deals', description: "We offer exclusive coupons and deals that you won't find anywhere else." },
    { title: 'User-Friendly Platform', description: 'Our website is designed to be intuitive and easy to navigate, ensuring a seamless experience.' },
  ];

  protected readonly faqs: TrustFaq[] = [
    {
      question: 'How do you ensure coupons are valid?',
      answer:
        'We use a combination of manual reviews, automated testing, and user feedback to ensure all coupons are valid and up-to-date.',
    },
    {
      question: "What should I do if a coupon doesn't work?",
      answer:
        "If a coupon doesn't work, please report it to us immediately. Our team will investigate and remove it if necessary.",
    },
    {
      question: 'How often are coupons updated?',
      answer: 'Our database is updated daily to ensure all coupons are current and valid.',
    },
    {
      question: 'Can I use multiple coupons at once?',
      answer:
        "It depends on the retailer's policy. Some stores allow combining coupons, while others do not. Check the terms and conditions for details.",
    },
  ];

  protected readonly expandedFaq = signal<number | null>(0);

  constructor() {
    // Fully static page — the constructor is the right place; the tags are in the DOM before the
    // server serialises the response.
    const url = this.seo.absolute(PAGE_PATH);

    this.seo.apply({
      title: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      canonicalPath: PAGE_PATH,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
      // These FAQs are rendered on the page (the accordion below), so FAQPage schema is honest here.
      faqPageSchema(this.faqs),
    ]);
  }

  protected toggleFaq(index: number): void {
    this.expandedFaq.update((current) => (current === index ? null : index));
  }
}
